// helpers/prodToken.js
//
// Cross-tab sync for the prod token ("jwtP").
//
// The prod token intentionally lives in sessionStorage so it dies when the last
// tab is closed (see LoginPage). The problem: sessionStorage is per-tab, so a
// tab opened via window.open("/route") starts EMPTY and loses the token. That
// caused prod requests to go out with `Bearer null` (annoying error toast) or to
// be silently skipped.
//
// Since the prod token is the only credential we hold (no refresh token, the
// password is never stored) it cannot be re-minted. So instead of persisting it
// to disk, a freshly-opened tab pulls it from an already-open sibling tab over a
// BroadcastChannel. Nothing is ever written to localStorage, so the original
// "token dies when the last tab closes" security property is preserved.

const KEY = "jwtP";
const CHANNEL_NAME = "prod-auth-sync";

const listeners = new Set();
let missing = false; // true once we've concluded the token is unrecoverable
let waiters = []; // pending ensureProdToken() resolvers
let inFlight = null; // shared in-flight request promise

const channel =
  typeof BroadcastChannel !== "undefined"
    ? new BroadcastChannel(CHANNEL_NAME)
    : null;

export const getProdToken = () => sessionStorage.getItem(KEY);

export const getProdTokenStatus = () => ({ missing });

export const subscribeProdToken = (cb) => {
  listeners.add(cb);
  return () => listeners.delete(cb);
};

const notify = () => {
  listeners.forEach((cb) => {
    try {
      cb({ missing });
    } catch (e) {
      /* ignore listener errors */
    }
  });
};

const setMissing = (next) => {
  if (missing === next) return;
  missing = next;
  notify();
};

const resolveWaiters = (token) => {
  const pending = waiters;
  waiters = [];
  pending.forEach((resolve) => resolve(token));
};

const writeToken = (token, { broadcast }) => {
    console.log("token, { broadcast }",token, { broadcast });
  if (!token) return;
  sessionStorage.setItem(KEY, token);
  setMissing(false);
  resolveWaiters(token);
  if (broadcast && channel) channel.postMessage({ type: "TOKEN", token });
};

// Call on successful login (and anywhere a fresh token is obtained) so every
// already-open tab picks it up too.
export const setProdToken = (token) => writeToken(token, { broadcast: true });

if (channel) {
  channel.onmessage = (e) => {
    const msg = e.data || {};
    if (msg.type === "REQUEST") {
      // A sibling needs the token; answer if we have it.
      const token = getProdToken();
      if (token) channel.postMessage({ type: "TOKEN", token });
    } else if (msg.type === "TOKEN" && msg.token) {
      if (msg.token !== getProdToken()) {
        writeToken(msg.token, { broadcast: false });
      } else {
        // Same token — still release any waiter that was blocked.
        setMissing(false);
        resolveWaiters(msg.token);
      }
    }
  };

  // Ask siblings for the token as early as possible if this tab has none.
  if (!getProdToken()) channel.postMessage({ type: "REQUEST" });
}

// Returns the prod token, hydrating from a sibling tab if this tab has none.
// Resolves to null (and flips the `missing` banner state) only when no sibling
// answers within `timeout` ms — i.e. the token is genuinely unrecoverable.
export const ensureProdToken = (timeout = 1200) => {
  const existing = getProdToken();
  if (existing) return Promise.resolve(existing);
  if (!channel) {
    setMissing(true);
    return Promise.resolve(null);
  }
  if (inFlight) return inFlight;

  inFlight = new Promise((resolve) => {
    let done = false;
    let timer;
    const finish = (token) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      inFlight = null;
      if (!token) setMissing(true);
      resolve(token || null);
    };
    waiters.push(finish);
    channel.postMessage({ type: "REQUEST" });
    timer = setTimeout(() => finish(getProdToken()), timeout);
  });
  return inFlight;
};
